import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ErrorService } from '../../services/error.service';
import { ModalController, NavController, ToastController } from '@ionic/angular';
import * as fromOrganizationActions from '../actions/organization.actions';
import { OrganizationService } from 'src/app/services/organization.service';
import { catchError, exhaustMap, filter, map, mergeMap, tap } from 'rxjs/operators';
import { failedTransaction, setLoadingSpinner } from '../actions/shared.actions';
import { APIResponse } from 'src/app/models/api-response.model';
import { of } from 'rxjs';
import { RequestOrgSuccessComponent } from 'src/app/shared/modals/request-org-success/request-org-success.component';
import { JoinOrgSuccessComponent } from 'src/app/shared/modals/join-org-success/join-org-success.component';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';
import { AppointmentQrComponent } from 'src/app/shared/modals/appointment-qr/appointment-qr.component';
import { CreateAppointmentSuccessComponent } from 'src/app/shared/modals/create-appointment-success/create-appointment-success.component';
import { ScanSuccessComponent } from 'src/app/shared/modals/scan-success/scan-success.component';

@Injectable()
export class OrganizationEffects {
  
  getUserOrganizationPoints$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrganizationActions.getUserOrganizationPoints),
      mergeMap(() => {
        return this.organizationService.userOrganizationPoints().pipe(
          map((response: any) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            console.log('getUserOrganizationPoints$', response);
            return fromOrganizationActions.getUserOrganizationPointsSuccess({
              data: response.details,
            });
          })
        );
      })
    );
  });

  getOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrganizationActions.getOrganization),
      mergeMap(() => {
        return this.organizationService.organizationList().pipe(
          map((response: any) => {
            console.log('getOrganization$', response);
            return fromOrganizationActions.getOrganizationSuccess({
              data: response.details,
            });
          })
        );
      })
    );
  });

  getUserOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrganizationActions.getUserOrganization),
      mergeMap(() => {
        return this.organizationService.userOrganization().pipe(
          map((response: any) => {
            console.log('Effects: Get User Organization', response);
            return fromOrganizationActions.getUserOrganizationSuccess({
              data: response.details,
            });
          })
        );
      })
    );
  });

  getPasses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrganizationActions.getUserPasses),
      mergeMap(() => {
        return this.organizationService.userPasses().pipe(
          map((response: APIResponse) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            console.log('Effects: Get Passes', response);
            return fromOrganizationActions.getUserPassesSuccess({data: response.details});
          })
        );
      })
    )
  );

  joinOrganization$ = createEffect(() =>
    {
      return this.actions$.pipe(
        ofType(fromOrganizationActions.joinOrganization),
        exhaustMap((action) => this.organizationService.joinOrganization(action.data).pipe(
          map((response) => {
            this.navCtrl.back();
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.store.dispatch(
              fromOrganizationActions.getUserOrganizationPoints()
            );
            return fromOrganizationActions.joinOrganizationSuccess({
              data: response,
              org: action?.org
            });
          }),
          catchError((response) => {
            console.error('joinOrganization$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
        )
      );
    }
  );

  joinOrganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.joinOrganizationSuccess),
        tap(async (action) => {
          this.store.dispatch(fromOrganizationActions.getOrganization());
          console.log('joinOrganizationSuccess$', action?.data)
          if(action?.org.autoAccept === 'yes'){
            this.joinSuccessModal(action?.org);
          }
          else if(action?.org.autoAccept === 'no'){
            this.requestSuccessModal(action?.org);
          }
        })
      ),
    { dispatch: false }
  );

  leaveOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrganizationActions.leaveOrganization),
      exhaustMap((action) => this.organizationService.leaveOrganization(action.data).pipe(
        map((response) => {

          this.store.dispatch(setLoadingSpinner({ status: false }));
          this.store.dispatch(
            fromOrganizationActions.getUserOrganizationPoints()
          );

          return fromOrganizationActions.leaveOrganizationSuccess({
            data : response,
            org : action.org
          })
        }),
        catchError((response)=> {
          console.error('leaveOrganization$', response);
          this.store.dispatch(setLoadingSpinner({status : false}));
          const error = {
            status : response.status,
            error : this.errorService.getErrorMessage(response)
          }
          return of(failedTransaction({data : error}));
        })
      ))
    )
  })

  leaveOrganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.leaveOrganizationSuccess),
        tap(async (action) => {
          
          this.store.dispatch(fromOrganizationActions.getUserOrganization());
          this.leaveSuccessModal(action?.org, "You have successfully left ");

        })
      ),
    { dispatch: false }
  );

  setActiveORganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrganizationActions.setActiveORganization),
      exhaustMap((action) =>
        this.organizationService.setHomeOrganization(action.data).pipe(
          map((response) => {
            this.store.dispatch(fromOrganizationActions.getOrganizationContent());
            this.store.dispatch(fromOrganizationActions.getUserOrganizationPoints());
            return fromOrganizationActions.setActiveORganizationSuccess({data: action?.organization})
          }),
          catchError((response) => {
            console.error('setActiveORganization$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  setActiveORganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.setActiveORganizationSuccess),
        tap(async (action) => {
          console.log('setActiveORganizationSuccess$', action?.data);
          
        })
      ),
    { dispatch: false }
  );

  scanStaticQr$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrganizationActions.scanStaticQr),
      exhaustMap((action) =>
        this.organizationService.scanStaticQr(action.data).pipe(
          map((response) => {
            console.log({response})
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.navCtrl.navigateBack('/home/bottom-tabs/dashboard');
            return fromOrganizationActions.scanStaticQrSuccess({data: response});
          }),
          catchError((response) => {
            console.error('scanStaticQr$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            this.navCtrl.navigateBack('/home/bottom-tabs/dashboard');
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  scanStaticQrSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.scanStaticQrSuccess),
        tap(async (action) => {
          console.log('scanStaticQrSuccess$', action);
          const modal = await this.modalController.create({
            component: ScanSuccessComponent,
            cssClass: 'scan-success-modal',
            componentProps: {
              routeType: 'security',
              status: 'success',
              message: 'Success',
              data: action?.data
            }
          });
          return modal.present();
        })
      ),
    { dispatch: false }
  );

  async joinSuccessModal(org?){
    const modal = await this.modalController.create({
      component: JoinOrgSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false,
      componentProps: {
        org
      }
    });

    return modal.present();
  }

  async leaveSuccessModal(org, description?){
    const modal = await this.modalController.create({
      component: JoinOrgSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false,
      componentProps: {
        org, description
      }
    });

    return modal.present();
  }

  async requestSuccessModal(org){
    const modal = await this.modalController.create({
      component: RequestOrgSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false,
      componentProps: {
        org
      }
    });

    return modal.present();
  }

  getAppointmentLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/dashboard');
      }),
      exhaustMap(() => {
        return this.organizationService.getAppointmentLocations().pipe(
          map((response: APIResponse) => {           
            console.log('getAppointmentLocations$', response);
            return fromOrganizationActions.getAppointmentLocationSuccess({data: response.details});
          })
        );
      })
    )
  });

  createAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrganizationActions.createAppointment),
      exhaustMap((action) =>
        this.organizationService.createAppointment(action.data).pipe(
          map((response) => {
            console.log({response})
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const details = {
              payload: action.data,
              response
            }
            return fromOrganizationActions.createAppointmentSuccess({data: details});
          }),
          catchError((response) => {
            console.error('createAppointment$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  createAppointmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.createAppointmentSuccess),
        tap(async (action) => {
          console.log('createAppointmentSuccess$', action);
          this.navCtrl.navigateBack('/home/bottom-tabs/calendar');
          if(action?.data?.payload?.guestType !== 'guest_name'){
            const modal = await this.modalController.create({
              component: CreateAppointmentSuccessComponent,
              cssClass: 'create-appointment-success-modal'
            });
        
            return modal.present();
          }
          else{
            const appointment = action?.data?.response?.details[0];
            const modal = await this.modalController.create({
              component: AppointmentQrComponent,
              componentProps: {
                appointment,
                fromResponse: true
              }
            });
        
            return modal.present();
          }
        })
      ),
    { dispatch: false }
  );

  getOrganizationContent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/dashboard');
      }),
      exhaustMap(() => {
        return this.organizationService.getOrganizationContent().pipe(
          map((response: APIResponse) => {           
            console.log('getOrganizationContent$', response);
            return fromOrganizationActions.getOrganizationContentSuccess({data: response.details});
          })
        );
      })
    )
  });

  reloadOrganizationContent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOrganizationActions.getOrganizationContent),
      exhaustMap(() => {
        return this.organizationService.getOrganizationContent().pipe(
          map((response: APIResponse) => {           
            console.log('reloadOrganizationContent$', response);
            return fromOrganizationActions.getOrganizationContentSuccess({data: response.details});
          })
        );
      })
    )
  });

  getUserFeedback$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/dashboard');
      }),
      mergeMap(() => {
        return this.organizationService.getPendingFeedback().pipe(
          map((response: any) => {
            console.log('getUserFeedback$', response);
            return fromOrganizationActions.getUserFeedbackSuccess({data: response.details});
          })
        );
      })
    );
  });

  giveFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOrganizationActions.userFeedback),
      exhaustMap((action) =>
        this.organizationService.userFeedback(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromOrganizationActions.userFeedbackSuccess({data: response});
          }),
          catchError((response) => {
            console.error('giveFeedback$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response),
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  giveFeedbackSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOrganizationActions.userFeedbackSuccess),
        tap(async (action) => {
          console.log('giveFeedbackSuccess$', action);
          this.presentToast('Your feedback have been submitted. Thank you!', 'middle', 'toastSuccess');
        })
      ),
    { dispatch: false }
  );

  async presentToast(msg, pos, style) {
    const toast = await this.toastController.create({
      message: msg,
      duration: 3000,
      position: pos,
      cssClass: style,
    });
    toast.present();
  }

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private organizationService: OrganizationService,
    private toastController: ToastController,
    private modalController: ModalController,
    private navCtrl: NavController
  ) {}
}
